/**
 * Usage History Table Component
 * 
 * Displays user's usage history including:
 * - Transaction history (purchases, usage, refunds)
 * - Filtering by type and date range
 * - Pagination
 * - Export functionality
 * - Visual indicators for different transaction types
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  Brain, 
  CreditCard, 
  Gift, 
  RotateCcw, 
  Settings, 
  Download,
  Calendar,
  Filter,
  Crown
} from 'lucide-react';
import type { CreditTransaction } from '../../types/usage';

interface UsageHistoryTableProps {
  transactions: CreditTransaction[];
  loading?: boolean;
  onExport?: () => void;
  className?: string;
}

type FilterType = 'all' | 'search' | 'ai_analysis' | 'purchase' | 'usage' | 'refund';
type DateRange = 'all' | '7d' | '30d' | '90d';

export function UsageHistoryTable({ 
  transactions, 
  loading = false, 
  onExport,
  className = "" 
}: UsageHistoryTableProps) {
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Filter and search logic
  const filteredTransactions = useMemo(() => {
    let filtered = transactions;

    // Filter by type
    if (filterType !== 'all') {
      if (filterType === 'search' || filterType === 'ai_analysis') {
        filtered = filtered.filter(t => 
          t.transaction_type === 'usage' && t.credit_type === filterType
        );
      } else {
        filtered = filtered.filter(t => t.transaction_type === filterType);
      }
    }

    // Filter by date range
    if (dateRange !== 'all') {
      const now = new Date();
      const daysBack = parseInt(dateRange.replace('d', ''));
      const cutoffDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));
      
      filtered = filtered.filter(t => 
        new Date(t.created_at) >= cutoffDate
      );
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.transaction_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.credit_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [transactions, filterType, dateRange, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getTransactionIcon = (transaction: CreditTransaction) => {
    if (transaction.transaction_type === 'purchase') {
      return <CreditCard className="w-4 h-4 text-green-600" />;
    }
    
    if (transaction.transaction_type === 'usage') {
      if (transaction.credit_type === 'search') {
        return <Search className="w-4 h-4 text-blue-600" />;
      } else {
        return <Brain className="w-4 h-4 text-blue-600" />;
      }
    }
    
    if (transaction.transaction_type === 'refund') {
      return <RotateCcw className="w-4 h-4 text-orange-600" />;
    }
    
    return <Settings className="w-4 h-4 text-muted-foreground" />;
  };

  const getTransactionBadge = (transaction: CreditTransaction) => {
    const isPositive = transaction.amount > 0;
    
    switch (transaction.transaction_type) {
      case 'purchase':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-300">
            Purchase
          </Badge>
        );
      case 'usage':
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-300">
            {transaction.credit_type === 'search' ? 'Search' : 'AI Analysis'}
          </Badge>
        );
      case 'refund':
        return (
          <Badge className="bg-orange-100 text-orange-800 border-orange-300">
            Refund
          </Badge>
        );
      case 'admin_adjustment':
        return (
          <Badge className="bg-secondary text-muted-foreground border-border">
            Admin
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {transaction.transaction_type}
          </Badge>
        );
    }
  };

  const getAmountDisplay = (transaction: CreditTransaction) => {
    const isPositive = transaction.amount > 0;
    const prefix = isPositive ? '+' : '';
    const color = isPositive ? 'text-green-600' : 'text-destructive';
    
    return (
      <span className={`font-medium ${color}`}>
        {prefix}{transaction.amount}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Usage History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse space-y-3 w-full">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Usage History
          </CardTitle>
          {onExport && (
            <Button onClick={onExport} variant="outline" size="sm" className="gap-2">
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          
          <Select value={filterType} onValueChange={(value: FilterType) => setFilterType(value)}>
            <SelectTrigger className="w-full sm:w-[140px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="search">Searches</SelectItem>
              <SelectItem value="ai_analysis">AI Analysis</SelectItem>
              <SelectItem value="purchase">Purchases</SelectItem>
              <SelectItem value="usage">Usage</SelectItem>
              <SelectItem value="refund">Refunds</SelectItem>
            </SelectContent>
          </Select>

          <Select value={dateRange} onValueChange={(value: DateRange) => setDateRange(value)}>
            <SelectTrigger className="w-full sm:w-[130px]">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results Summary */}
        <div className="text-sm text-muted-foreground">
          Showing {paginatedTransactions.length} of {filteredTransactions.length} transactions
          {filterType !== 'all' || dateRange !== 'all' || searchTerm ? (
            <span> (filtered)</span>
          ) : null}
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Date/Time</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {searchTerm || filterType !== 'all' || dateRange !== 'all' 
                      ? 'No transactions match your filters.' 
                      : 'No transaction history available.'
                    }
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTransactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell className="text-sm">
                      {formatDate(transaction.created_at)}
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTransactionIcon(transaction)}
                        {getTransactionBadge(transaction)}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm">
                        {transaction.description}
                      </div>
                      {transaction.workspace_id && (
                        <div className="text-xs text-muted-foreground">
                          Workspace: {transaction.workspace_id.slice(0, 8)}...
                        </div>
                      )}
                    </TableCell>
                    
                    <TableCell className="text-right">
                      {getAmountDisplay(transaction)}
                    </TableCell>
                    
                    <TableCell className="text-right">
                      <span className="font-medium">
                        {transaction.balance_after}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              
              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + Math.max(1, currentPage - 2);
                  if (page > totalPages) return null;
                  
                  return (
                    <Button
                      key={page}
                      variant={page === currentPage ? "default" : "outline"}
                      size="sm"
                      className="w-8 h-8"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}